// =============================================================================
// ApiCostsPage — per-request LLM cost dashboard
// =============================================================================
// Admin-only (cesarzdarek@gmail.com). Zrodlo: llm_call_events tabela,
// zapisywana przez hook w openaiProvider + claudeProvider.
//
// Pokazuje:
//   - 4 scorecards (dzis / wczoraj / tydzien / miesiac) z delta vs poprzedni okres
//   - Stacked area chart: koszt dzienny ostatnie 30 dni per model
//   - Top 10 najdrozszych steps (14 dni)
//   - Top 10 najdrozszych map (14 dni)
//   - Refresh button + last-fetched timestamp
//
// Uwaga: computed cost (estimateCost × cennik), nie actual bill od providera.
// Rozjazd ~5-15% przez cached rate + discounts. Ground truth z Admin API
// jest odkladana na osobna sesje (Faza 2).

import { useMemo } from "react";
import { TopBar } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom";
import { RefreshCw, TrendingUp, TrendingDown, DollarSign, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { useApiCosts, type PeriodTotals } from "@/hooks/useApiCosts";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { WidgetErrorBoundary } from "@/components/WidgetErrorBoundary";

// Stable palette per model (deterministic z hash modelName)
const MODEL_COLORS: Record<string, string> = {
  "gpt-5.4": "#8b5cf6",
  "gpt-5.4-mini": "#a78bfa",
  "gpt-4.1": "#3b82f6",
  "gpt-4.1-mini": "#60a5fa",
  "gpt-4.1-nano": "#93c5fd",
  "claude-opus-4-7": "#ec4899",
  "claude-opus-4-6": "#f472b6",
  "claude-sonnet-5": "#f59e0b",
  "claude-opus-5": "#ef4444",
  "claude-sonnet-4-6": "#fbbf24",
  "claude-sonnet-4-5": "#fbbf24",
  "claude-haiku-4-5": "#10b981",
};
const FALLBACK_PALETTE = ["#6366f1", "#14b8a6", "#f43f5e", "#0ea5e9", "#eab308", "#a855f7"];
function colorFor(model: string, idx: number): string {
  return MODEL_COLORS[model] ?? FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length];
}

function formatUsd(n: number): string {
  if (n === 0) return "$0.00";
  if (n < 0.001) return "<$0.001";
  if (n < 1) return `$${n.toFixed(3)}`;
  if (n < 100) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(0)}`;
}

function Delta({ current, previous }: { current: number; previous: number }) {
  if (previous === 0) {
    if (current === 0) return <span className="text-muted-foreground text-xs">—</span>;
    return <span className="text-xs text-amber-600 dark:text-amber-400">new</span>;
  }
  const pct = ((current - previous) / previous) * 100;
  const up = pct > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const color = up ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ${color}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

function ScoreCard({ label, value, previous, hint }: { label: string; value: number; previous: number; hint: string }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <Delta current={value} previous={previous} />
      </div>
      <p className="text-2xl font-semibold tabular-nums">{formatUsd(value)}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>
    </Card>
  );
}

export default function ApiCostsPage() {
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const { totals, daily, topSteps, topMaps, loading, refresh, lastFetchedAt } = useApiCosts();

  // Transform daily into recharts format: [{ date: 'YYYY-MM-DD', 'gpt-4.1': 0.12, 'claude-sonnet-4-6': 0.04, ... }]
  const { chartData, models } = useMemo(() => {
    const dateMap = new Map<string, Record<string, number>>();
    const modelSet = new Set<string>();
    for (const row of daily) {
      modelSet.add(row.model);
      const bucket = dateMap.get(row.date) ?? { date: row.date };
      bucket[row.model] = (bucket[row.model] as number ?? 0) + row.cost;
      dateMap.set(row.date, bucket as Record<string, number>);
    }
    const data = [...dateMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([_, v]) => v);
    return { chartData: data, models: [...modelSet].sort() };
  }, [daily]);

  const totalMonth = totals.month;
  const avgDaily = totalMonth / 30;
  const projectedMonth = avgDaily * 30;  // Linear projection (same as current if we have 30d of data)

  // Detekcja zupelnie pustego stanu (zadnych danych)
  const hasAnyData = totals.month > 0 || totals.week > 0 || totals.today > 0 || totals.yesterday > 0 || daily.length > 0;

  if (adminLoading) {
    return (
      <>
        <TopBar title="Koszty API" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  if (!isAdmin) {
    return (
      <>
        <TopBar title="Koszty API" />
        <div className="flex-1 p-6">
          <Card className="p-8 text-center text-muted-foreground">
            <AlertTriangle className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p>Widok admin-only. Brak dostepu.</p>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar title="Koszty API" />
      <div className="flex-1 p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Koszty API — per-request tracking
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Computed cost (cennik × tokens) z <code className="text-xs bg-muted px-1 rounded">llm_call_events</code>.
              Rozjazd do actual billu providera: <strong>~5-15%</strong> (cached rate + discounts).
              Ground truth z Admin API — odrebna sesja.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {lastFetchedAt && (
              <span className="text-xs text-muted-foreground">
                {lastFetchedAt.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              Odswiez
            </Button>
          </div>
        </div>

        {/* Empty state — czesty przypadek bezposrednio po deployu (bundle jeszcze bez trackLlmCall) */}
        {!loading && !hasAnyData && (
          <Card className="p-5 border-amber-500/40 bg-amber-500/5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="space-y-1.5 text-sm">
                <p className="font-medium text-foreground">Brak danych w tabeli <code className="text-xs bg-muted px-1 rounded">llm_call_events</code>.</p>
                <p className="text-muted-foreground">
                  Jesli wlasnie zdeployowales frontend — przejdz przez pipeline (np. wygeneruj strukture
                  jednego noda w Content Engine) i wroc tutaj. Hook <code className="text-xs bg-muted px-1 rounded">trackLlmCall</code> w providerach
                  OpenAI/Claude zapisuje kazdy call.
                </p>
                <p className="text-muted-foreground">
                  Jesli frontend <strong>NIE byl zdeployowany</strong> po commit{" "}
                  <code className="text-xs bg-muted px-1 rounded">8ffd382</code> — zrob Redeploy
                  w Coolify, odswiez karte (Ctrl+Shift+R), potem wykonaj cokolwiek LLM.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Scorecards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ScoreCard label="Dzis" value={totals.today} previous={totals.yesterday} hint="vs wczoraj" />
          <ScoreCard label="Wczoraj" value={totals.yesterday} previous={0} hint="dla porownania" />
          <ScoreCard label="Tydzien" value={totals.week} previous={totals.prevWeek} hint="7 dni vs poprzednie 7" />
          <ScoreCard label="Miesiac" value={totals.month} previous={totals.prevMonth} hint="30 dni vs poprzednie 30" />
        </div>

        {/* Summary: avg daily + projection */}
        <Card className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm">
            <span className="text-muted-foreground">Srednia dzienna (ost. 30 dni):</span>{" "}
            <span className="font-semibold tabular-nums">{formatUsd(avgDaily)}</span>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Projekcja miesieczna (linear):</span>{" "}
            <span className="font-semibold tabular-nums">{formatUsd(projectedMonth)}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Aktywne modele: <strong>{models.length}</strong>
          </div>
        </Card>

        {/* Stacked area chart — daily per model */}
        <Card className="p-4">
          <h2 className="text-sm font-medium mb-3">Koszt dzienny per model (30 dni, stacked)</h2>
          {chartData.length === 0 || models.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Brak danych — zrob kilka calli przez pipeline aby wypelnic."}
            </div>
          ) : (
            <WidgetErrorBoundary name="ApiCostsAreaChart">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" className="text-xs" tickFormatter={(d) => d.slice(5)} />
                  <YAxis className="text-xs" tickFormatter={(v) => `$${v.toFixed(2)}`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                    formatter={(value: number, name: string) => [formatUsd(value), name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {models.map((model, idx) => (
                    <Area
                      key={model}
                      type="monotone"
                      dataKey={model}
                      stackId="1"
                      stroke={colorFor(model, idx)}
                      fill={colorFor(model, idx)}
                      fillOpacity={0.6}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </WidgetErrorBoundary>
          )}
        </Card>

        {/* Top tables grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top steps */}
          <Card className="p-4">
            <h2 className="text-sm font-medium mb-3">Top 10 najdrozsze kroki (ostatnie 14 dni)</h2>
            <div className="border rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Step</TableHead>
                    <TableHead className="text-right w-[90px]">Calls</TableHead>
                    <TableHead className="text-right w-[90px]">Avg</TableHead>
                    <TableHead className="text-right w-[90px]">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topSteps.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-6">Brak danych</TableCell></TableRow>
                  ) : (
                    topSteps.map((s) => (
                      <TableRow key={s.step ?? "(unknown)"}>
                        <TableCell className="text-xs">
                          {s.step ?? <span className="text-muted-foreground italic">(bez step)</span>}
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums">{s.calls}</TableCell>
                        <TableCell className="text-right text-xs tabular-nums text-muted-foreground">{formatUsd(s.avg_cost)}</TableCell>
                        <TableCell className="text-right text-xs tabular-nums font-medium">{formatUsd(s.cost)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Top maps */}
          <Card className="p-4">
            <h2 className="text-sm font-medium mb-3">Top 10 najdrozsze mapy (ostatnie 14 dni)</h2>
            <div className="border rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mapa</TableHead>
                    <TableHead className="text-right w-[90px]">Calls</TableHead>
                    <TableHead className="text-right w-[90px]">Koszt</TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topMaps.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-6">Brak danych</TableCell></TableRow>
                  ) : (
                    topMaps.map((m) => (
                      <TableRow key={m.map_id}>
                        <TableCell className="text-xs truncate max-w-[220px]">
                          {m.map_name || <span className="text-muted-foreground font-mono text-[10px]">{m.map_id.slice(0, 8)}</span>}
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums">{m.calls}</TableCell>
                        <TableCell className="text-right text-xs tabular-nums font-medium">{formatUsd(m.cost)}</TableCell>
                        <TableCell>
                          <Link to={`/mapy-tematyczne/${m.map_id}`} className="text-muted-foreground hover:text-primary">
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>

        {/* Disclaimer */}
        <div className="text-[11px] text-muted-foreground border-t pt-3 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
          <p>
            Panel pokazuje <strong>computed cost</strong> z cennika × tokens. Actual bill od Anthropic/OpenAI moze
            roznic sie o <strong>5-15%</strong> ze wzgledu na cached rate (50% discount), batch API (50% discount)
            i inne promocje. <strong>Claude Code</strong> (subscription) nie pojawia sie tutaj — widoczny tylko w
            Anthropic Console. Per-provider ground truth z Admin API — zaplanowane w fazie 2.
          </p>
        </div>
      </div>
    </>
  );
}
